import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import type { ColDef, GridOptions } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { NeonButton, NeonPagination, NeonSearch, NeonTooltip } from '@ps-refarch-ux/neon';
import { DataGrid } from '@/components/DataGrid/data-grid';
import { EmptyState } from '@/components/EmptyState/empty-state';
import { TableSkeleton } from '@/components/TableSkeleton/table-skeleton';
import { usePageConfig } from '@/layout-templates';
import { SortableHeader } from '@/components/sortable-header';
import { NeonButtonTypes } from '@/constants/neon-constants';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import {
  useDeleteRecommendation,
  useGetRecommendedCourses,
  type RecommendedCourse,
} from '@/repositories/recommendations-repository';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import { getHomeUrl } from '@/utils/cp-context';
import { DeleteRecommendationModal } from './delete-recommendation-modal';
import styles from './recommendations.module.css';
import './recommendations.styles.css';
import { formatRecommendationDate } from './recommendations.utils';

const DEFAULT_PAGE_SIZE = 25;

/** Displays the school-scoped courses with current student recommendations. */
export function Recommendations(): ReactElement {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const filters = useMemo(
    () => ({
      page,
      pageSize,
      ...(searchTerm.trim() ? { search: searchTerm } : {}),
    }),
    [page, pageSize, searchTerm]
  );
  const { data, error, isLoading, isFetching } = useGetRecommendedCourses(filters);

  const { showToast } = useToastNotifications();
  const deleteRecommendation = useDeleteRecommendation();
  const [recommendationToDelete, setRecommendationToDelete] = useState<RecommendedCourse | null>(
    null
  );

  const handleConfirmDelete = useCallback(() => {
    if (!recommendationToDelete || deleteRecommendation.isPending) {
      return;
    }
    const { courseName } = recommendationToDelete;
    deleteRecommendation.mutate(recommendationToDelete.courseId, {
      onSuccess: () => {
        setRecommendationToDelete(null);
        showToast('success', { heading: `${courseName} recommendation deleted` });
      },
      onError: () => {
        setRecommendationToDelete(null);
        showToast('error', { heading: 'An error occurred' });
      },
    });
  }, [deleteRecommendation, recommendationToDelete, showToast]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (data && data.totalPages > 0 && page > data.totalPages) {
      setPage(data.totalPages);
    }
  }, [data, page]);

  usePageConfig({
    breadcrumbs: [
      { text: 'Home', url: getHomeUrl() },
      { text: 'Recommendations', url: '#' },
    ],
    breadcrumbsId: 'recommendations-breadcrumbs',
    title: 'Recommendations',
    action: {
      id: 'add-recommendation',
      text: 'Add recommendation',
      icon: 'add',
      type: NeonButtonTypes.Primary,
      onClick: () => navigate(`/${STAFF_ROUTES.recommendations.add}`),
    },
  });

  const columnDefs = useMemo<ColDef<RecommendedCourse>[]>(
    () => [
      {
        field: 'courseId',
        headerName: 'Course ID',
        flex: 1,
        minWidth: 150,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        headerComponent: SortableHeader,
      },
      {
        field: 'courseName',
        headerName: 'Course name',
        flex: 2,
        minWidth: 220,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        headerComponent: SortableHeader,
        initialSort: 'asc',
        comparator: (a: string, b: string) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
      },
      {
        field: 'lastAddedAt',
        headerName: 'Last added to',
        flex: 1,
        minWidth: 150,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        headerComponent: SortableHeader,
        comparator: (firstDate: string | undefined, secondDate: string | undefined) =>
          (Date.parse(firstDate ?? '') || 0) - (Date.parse(secondDate ?? '') || 0),
        cellRenderer: (props: { data: RecommendedCourse }) =>
          formatRecommendationDate(props.data.lastAddedAt),
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        flex: 1,
        minWidth: 100,
        sortable: false,
        cellClass: styles.actionsCell,
        headerClass: 'recommendations-actions-header',
        cellRenderer: (props: { data: RecommendedCourse }) => (
          <div className={styles.actionButtons}>
            <NeonButton
              id={`edit-recommendation-${props.data.courseId}`}
              dataIcon="edit"
              dataType="utility"
              dataText="Edit"
              dataShowText={false}
              dataAriaLabel={`Edit recommendation for ${props.data.courseName}`}
              onClick={() =>
                navigate(
                  `/${STAFF_ROUTES.recommendations.edit(props.data.courseId)}?courseName=${encodeURIComponent(props.data.courseName)}`
                )
              }
            />
            <NeonTooltip dataTargetId={`edit-recommendation-${props.data.courseId}`}>
              Edit
            </NeonTooltip>
            <NeonButton
              id={`delete-recommendation-${props.data.courseId}`}
              dataIcon="delete"
              dataType="utility"
              dataText="Delete"
              dataShowText={false}
              dataAriaLabel={`Delete recommendation for ${props.data.courseName}`}
              onClick={() => setRecommendationToDelete(props.data)}
            />
            <NeonTooltip dataTargetId={`delete-recommendation-${props.data.courseId}`}>
              Delete
            </NeonTooltip>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const gridOptions = useMemo<GridOptions<RecommendedCourse>>(
    () => ({
      suppressCellFocus: true,
      suppressMovableColumns: true,
      suppressHorizontalScroll: true,
      defaultColDef: { resizable: false, sortable: true, suppressMovable: true },
    }),
    []
  );

  const isTableLoading = !error && (isLoading || isFetching);
  const tableSkeleton = (
    <TableSkeleton
      columns={['16%', '32%', '22%', '18%']}
      rowCount={8}
      testId="recommendations-skeleton"
      ariaLabel="Loading course recommendations"
    />
  );

  if (isLoading && !data) {
    return (
      <section className={styles.container} aria-label="Course recommendations">
        {tableSkeleton}
      </section>
    );
  }

  const recommendations = [...(data?.data ?? [])].sort((firstCourse, secondCourse) =>
    firstCourse.courseName.localeCompare(secondCourse.courseName, undefined, {
      sensitivity: 'base',
    })
  );
  const totalRecommendations = data?.total ?? 0;
  const isSearchActive = Boolean(searchTerm.trim());
  const isUnfilteredEmpty =
    !error && !isTableLoading && !isSearchActive && totalRecommendations === 0;
  return (
    <section className={styles.container} aria-label="Course recommendations">
      <DataGrid<RecommendedCourse>
        id="recommendations"
        columnDefs={columnDefs}
        rowData={error || isTableLoading ? [] : recommendations}
        domLayout="normal"
        gridHeight="min(600px, 60vh)"
        rowHeight={50}
        headerHeight={50}
        cardWrapper={true}
        childrenInsideCard={true}
        className="neon-data-grid recommendations-grid"
        gridOptions={gridOptions}
        alwaysShowLayout={true}
        emptyRowsTreatAsFiltered={!error && isSearchActive}
        filter={{ title: '', fields: [], onFieldChange: () => {}, hideToggleButton: true }}
        emptyState={
          isTableLoading ? (
            tableSkeleton
          ) : error ? (
            <EmptyState title="Unable to load recommendations" body="Try refreshing the page." />
          ) : (
            <EmptyState title="No course recommendations yet" body="" />
          )
        }
        emptyStateWhenFiltered={
          isTableLoading ? tableSkeleton : <EmptyState title="No results found" body="" />
        }
        actionBar={
          isUnfilteredEmpty ? undefined : (
            <>
              <span className={styles.count} aria-live="polite">
                {totalRecommendations}{' '}
                {totalRecommendations === 1 ? 'course recommendation' : 'course recommendations'}
              </span>
              <div className={styles.searchField}>
                <NeonSearch
                  id="recommendations-search"
                  dataSize="large"
                  dataPlaceholderText="Search by course name or ID"
                  value={searchTerm}
                  neonSearchValueChange={setSearchTerm}
                />
              </div>
            </>
          )
        }
      >
        {!error && !isTableLoading && totalRecommendations > DEFAULT_PAGE_SIZE && (
          <form onSubmit={(event) => event.preventDefault()} className={styles.paginationForm}>
            <div onClickCapture={(event) => event.preventDefault()}>
              <NeonPagination
                id="recommendations-pagination"
                dataArrayLength={totalRecommendations}
                dataCurrentPage={page}
                dataPageLength={pageSize}
                dataPageSizeOptions="25,50,100,200"
                dataShowJumpTo={true}
                dataShowResults={true}
                neonPaginationChange={(nextPage, nextPageSize) => {
                  setPage(nextPage);
                  setPageSize(nextPageSize);
                }}
              />
            </div>
          </form>
        )}
      </DataGrid>
      <DeleteRecommendationModal
        isOpen={recommendationToDelete !== null}
        modalId="delete-recommendation-modal"
        courseName={recommendationToDelete?.courseName ?? ''}
        isDeleting={deleteRecommendation.isPending}
        onClose={() => setRecommendationToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </section>
  );
}
